import { useEffect } from "react";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Whitelist of event parameter keys that are safe to forward to GA4.
 * Explicitly excludes PII (emails, phone numbers, names, free-form input).
 */
const GA4_ALLOWED_KEYS = new Set([
  "page",
  "page_path",
  "destination",
  "destination_url",
  "external",
  "link_text",
  // Placement of the CTA within the page ("hero_primary", "services_leagues",
  // …). Without it every booking click on a page collapses into one
  // undifferentiated count, so GA4 can tell you the homepage converts but not
  // which button did it. It is always a hardcoded literal, never user input.
  "location",
  "service_type",
  "threshold",
  "category",
  "slug",
  // Event inquiry details (event_inquiry_submitted). form_location is a
  // hardcoded literal and event_type comes from the form's fixed select list.
  // estimated_guests is user input, so it is forwarded only when it is a
  // plain number (see sanitizeForGa4).
  "form_location",
  "event_type",
  "estimated_guests",
]);

function sanitizeForGa4(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (!GA4_ALLOWED_KEYS.has(k)) continue;
    if (v === null || v === undefined) continue;
    if (k === "estimated_guests") {
      // Free-text field: forward only a bare guest count, never other text.
      const n = String(v).trim();
      if (/^\d{1,4}$/.test(n)) out[k] = Number(n);
      continue;
    }
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") {
      out[k] = v;
    }
  }
  if (!("page_path" in out) && typeof window !== "undefined") {
    out.page_path = window.location.pathname;
  }
  return out;
}

/**
 * Push an event into the GTM dataLayer and mirror it to GA4 via gtag when
 * available. Safe no-op if neither has loaded yet (the inline GTM snippet in
 * index.html creates window.dataLayer immediately, and the inline GA4 snippet
 * defines window.gtag immediately).
 *
 * Only a whitelist of non-PII primitive params is forwarded to GA4.
 */
export function trackEvent(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", event, sanitizeForGa4(params));
    } catch {
      // ignore
    }
  }
}

/** Convenience: tel: link click handler factory. */
export function trackPhoneClick(number: string) {
  return () => trackEvent("phone_click", { number });
}

/**
 * Fire GA4-style scroll_depth events at the given thresholds (percent of
 * document scrolled). Each threshold fires at most once per mount.
 */
export function useScrollDepth(
  thresholds: number[] = [50],
  eventName: string = "scroll_depth",
  extra: Record<string, unknown> = {},
) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fired = new Set<number>();
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      if (total <= 0) return;
      const pct = Math.round((h.scrollTop / total) * 100);
      for (const t of thresholds) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent(eventName, { threshold: t, path: window.location.pathname, ...extra });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds.join(","), eventName]);
}

export {};
